import ArrowLeft from "@/assets/icons/ArrowLeft";
import Close from "@/assets/icons/Close";
import InfoLine from "@/assets/icons/InfoLine";
import AppButton from "@/components/shared/AppButton";
import { Box } from "@/components/ui/box";
import { Divider } from "@/components/ui/divider";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { Input, InputField } from "@/components/ui/input";
import { Modal, ModalBackdrop, ModalBody, ModalCloseButton, ModalContent, ModalFooter } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useRatePassphraseCheck } from "@/hooks/useRatePassphraseCheck";
import { T, useT } from "@transifex/react";
import { useRouter } from "expo-router";
import { RatePassphraseFailReason } from "ipctg-inflow-types/InFlowEnums";
import type { ParkRatePassphrasePOSTErrorResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { memo, useState } from "react";

interface PassphraseModalProps {
  isOpen: boolean;
  onClose: () => void;
  rateID: string;
  rateName: string;
}

const PassphraseModal = ({ isOpen, onClose, rateID, rateName }: PassphraseModalProps) => {
  const t = useT();
  const router = useRouter();
  const [passphrase, setPassphrase] = useState("");
  const [isInvalid, setIsInvalid] = useState(false);

  const { mutate, status, error } = useRatePassphraseCheck();

  const handleSubmit = () => {
    mutate(
      { rateID, passphrase },
      {
        onSuccess: () => {
          onClose();
          router.push({
            params: { isPurchasingPass: "false", rateID, rateName },
            pathname: "/cart",
          });
        },
        onError: () => setIsInvalid(true),
      },
    );
  };

  const handleFocus = () => {
    if (isInvalid) {
      setIsInvalid(false);
    }
  };

  const handleBack = () => {
    handleClose();
  };

  const handleClose = () => {
    setPassphrase("");
    setIsInvalid(false);
    onClose();
  };

  const isLoading = status === "pending";
  const errorMessage =
    error && "failReason" in error && (error as ParkRatePassphrasePOSTErrorResponse).failReason === RatePassphraseFailReason.InvalidPassphrase
      ? t("Please enter a valid passphrase.")
      : t("Something went wrong, please try again later");

  return (
    <Modal isOpen={isOpen} size="lg">
      <ModalBackdrop />
      <ModalContent className="bg-background-100 max-w-lg mx-auto rounded-[16px] pt-[32px] px-0 pb-[16px] flex flex-col justify-center">
        <ModalCloseButton onPress={handleClose} className="absolute top-4 right-4 z-10" data-test-id="close-button">
          <Close height={26} fill="text-typography-900" width={26} />
        </ModalCloseButton>

        <ModalBody className="flex flex-col justify-center gap-4 px-[20px]">
          <VStack className="flex flex-col items-center gap-4">
            <InfoLine stroke="bg-typography-900" fill="none" height={25} width={25} />
            <Heading size="lg" className="text-center mb-2 font-bold text-[18px] leading-[100%]">
              <T _str="Enter Passphrase" />
            </Heading>
            <Text className="text-center text-typography-900 mb-6 text-[16px] leading-[22px] font-normal">
              <T _str="This parking rate requires a passphrase to complete the order. Please enter the passphrase below." />
            </Text>
          </VStack>

          <VStack className="w-full">
            <Text className="pb-3 text-typography-900 font-semibold text-[14px] leading-[20px]">
              <T _str="Passphrase" />
            </Text>

            <Input
              className={`h-[52px] pt-[8px] pb-[8px] pl-[8px] gap-[8px] rounded-[6px] border-[1px] ${isInvalid ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
            >
              <InputField
                data-test-id="passphrase-input"
                placeholder={"*********"}
                autoCapitalize="characters"
                className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                value={passphrase}
                onChangeText={(text: string) => {
                  setPassphrase(text);
                  setIsInvalid(false);
                }}
                onFocus={handleFocus}
              />
            </Input>
            {isInvalid && <Text className="text-error-100 text-[14px] leading-[20px] mt-2">{errorMessage}</Text>}
          </VStack>
        </ModalBody>

        <Box className="pb-5 pt-1 w-full ">
          <Divider className="w-full bg-secondary-600" />
        </Box>

        <ModalFooter className="w-full px-[20px] pb-1">
          <HStack space="md" className="w-full">
            <AppButton className="flex-1" variant="outline" onPress={handleBack} icon={ArrowLeft} title={t("Back")} />
            <AppButton className="flex-1" onPress={handleSubmit} variant="primary" title={t("Submit")} loading={isLoading} />
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default memo(PassphraseModal);
